import { Capacitor } from "@capacitor/core";
import { FirebaseMessaging, GetTokenOptions } from "@capacitor-firebase/messaging";
import { appConfig } from "../configs";

// Global notification function - will be set by the app
let showNotification: ((notification: { type: 'success' | 'error' | 'warning' | 'info'; title: string; message?: string; duration?: number; action?: { label: string; onClick: () => void } }) => void) | null = null;

// Global token registration function - will be set by the app
let sendTokenToBackend: ((token: string, platform: 'android' | 'ios' | 'web') => Promise<void>) | null = null;

// Function to set the notification function from the app
export function setNotificationHandler(handler: typeof showNotification) {
  showNotification = handler;
}

// Function to set the token registration function from the app
export function setTokenRegistrationHandler(handler: typeof sendTokenToBackend) {
  sendTokenToBackend = handler;
}

export async function initPushMessaging() {
  // 1. Capacitor-Firebase listeners (foreground / tap)
  FirebaseMessaging.addListener("notificationReceived", (info) => {
    console.log("📩 notificationReceived:", info);
    
    // Show notification using our global notification system
    if (showNotification && Capacitor.getPlatform() !== 'ios' && info.notification) {
      const { title, body } = info.notification;
      
      showNotification({
        type: 'info', // Default to info for push notifications
        title: title || 'New Notification',
        message: body,
        duration: 5000, // Show for 5 seconds
      });
    }
  });
  
  FirebaseMessaging.addListener("notificationActionPerformed", (action) => {
    console.log("👉 notificationActionPerformed:", action);
    
    // Handle notification tap/action
    if (action.notification) {
      console.log('Notification tapped:', action.notification);
      // You can add navigation logic here based on notification content
    }
  });

  // 2. Token refresh listener
  FirebaseMessaging.addListener("tokenReceived", async (event) => {
    console.log("🔄 Token received/refreshed:", event);
    
    // Send updated token to backend using the same mechanism
    if (sendTokenToBackend && event.token) {
      const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 
                      Capacitor.getPlatform() === 'android' ? 'android' : 'web';
      
      try {
        await sendTokenToBackend(event.token, platform);
        console.log(`✅ Token registered for ${platform}`);
      } catch (error) {
        console.error(`❌ Failed to register token for ${platform}:`, error);
      }
    }
  });

  // 2. Native (iOS/Android) flow - only setup listeners, don't request permission
  if (Capacitor.isNativePlatform()) {
    // Don't request permission here - let the modal handle it
    console.log("📱 Native platform detected - permission will be requested via modal");
  }

  // 3. Web flow (Vite + SW + VAPID)
  if (Capacitor.getPlatform() === "web" && "serviceWorker" in navigator) {
    try {
      // a) Register SW
      const registration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js"
      );
      console.log("✅ SW registered:", registration);

      // b) Don't request permission here - let the modal handle it
      console.log("📱 Web platform detected - permission will be requested via modal");

      // c) Listen for raw SW messages (background)
      navigator.serviceWorker.addEventListener("message", (event: any) => {
        // FCM compat posts under 'firebase-messaging-msg-data'
        const data = event.data?.["firebase-messaging-msg-data"] ?? event.data;
        if (data?.notification) {
          const { title, body } = data.notification;
          new Notification(title, { body }).onclick = (e) => {
            console.log("🔔 notification click:", e);
          };
        }
      });
    } catch (e) {
      console.error("⚠️ Web FCM error:", e);
    }
  }
}

// Function to get FCM token for web when permission is granted
export async function getWebFCMToken(registration?: ServiceWorkerRegistration) {
  if (Capacitor.getPlatform() !== "web") return null;
  
  try {
    if (!registration) {
      registration = await navigator.serviceWorker.register("/firebase-messaging-sw.js");
    }
    
    const options: GetTokenOptions = {
      vapidKey: appConfig.firebase.vapidKey,
      serviceWorkerRegistration: registration,
    };
    const { token } = await FirebaseMessaging.getToken(options);
    console.log("🔑 Web FCM token:", token);
    // send token to your backend…
    return token;
  } catch (e) {
    console.error("⚠️ Error getting Web FCM token:", e);
    return null;
  }
} 